// dwaar/src/components/Koshtha.jsx
// Repo: terralekha-core | Folder: dwaar/src/components/
// Koshtha — Sanskrit: कोष्ठ — chamber, compartment, container.
//   Shared layout component: 240 px left sidebar + scrollable content area.
//   Used by every authenticated portal page (replaces per-page Navbar).
//
// Props:
//   role          ("customer" | "admin")  — determines which nav items are shown
//   userEmail     (string, optional)       — displayed in sidebar footer
//   onSignOut     (function, optional)     — called when Sign out is clicked
//   children      (ReactNode)             — page content, rendered in content column
//   passportId    (string, optional)       — forwarded to OraPanel for passport context
//   accessToken   (string, optional)       — forwarded to OraPanel for Bearer auth
//   cnCode        (string, optional)       — forwarded to OraPanel for CN context line
//   passportStatus(string, optional)       — forwarded to OraPanel for status line
//   hideOra       (boolean, default false) — suppresses sidebar OraPanel
//                                           (set true on Samiksha where Ora is in content)
//   complianceScope (string[], default ["CBAM"]) — compliance frameworks active for this org;
//                                                   drives conditional nav items + pills
//
// Active state:
//   useMatch() determines if the link path matches the current location.
//   aria-current="page" is set manually on the active <Link>.
//   React Router's NavLink does not support aria-current as a function prop.
//   Items marked soon:true render as <span> (not a link) with muted styling.

import React from "react";
import { Link, useMatch } from "react-router-dom";
import OraPanel from "./OraPanel.jsx";
import { BRAND_NAME } from "../lib/brand.js";

// ── Nav configurations ─────────────────────────────────────────────────────────

const BASE_CUSTOMER_NAV = [
  { key: "dashboard",      label: "Dashboard",     to: "/dashboard"    },
  { key: "new-passport",   label: "New PassPort",  to: "/passport/new" },
  { key: "confirmations",  label: "Confirmations", to: "#", soon: true },
  { key: "download",       label: "Download PDF",  to: "#", soon: true },
];

const BRSR_NAV_ITEMS = [
  { key: "brsr-report",    label: "BRSR Report",   to: "#", soon: true },
];

const CCTS_NAV_ITEMS = [
  { key: "carbon-credits", label: "Carbon Credits",to: "#", soon: true },
];

function buildCustomerNav(scope) {
  let nav = [...BASE_CUSTOMER_NAV];
  if (scope.includes("BRSR")) nav = [...nav, ...BRSR_NAV_ITEMS];
  if (scope.includes("CCTS")) nav = [...nav, ...CCTS_NAV_ITEMS];
  return nav;
}

const ADMIN_NAV = [
  { key: "health",    label: "Health",         to: "/supervisor"   },
  { key: "orgs",      label: "Organisations",  to: "#", soon: true },
  { key: "passports", label: "PassPorts",      to: "#", soon: true },
  { key: "engines",   label: "Engines",        to: "#", soon: true },
  { key: "revenue",   label: "Revenue",        to: "#", soon: true },
];

const AUDITOR_NAV = [
  { key: "audit-passports", label: "Passports", to: "/audit" },
];

// ── NavItem ───────────────────────────────────────────────────────────────────

function NavItem({ item }) {
  // useMatch must be called unconditionally (hooks rule).
  // Soon items use a placeholder path that never matches any real URL.
  const safePath = item.soon ? "/__ks_soon_nomatch__" : item.to;
  const match    = useMatch({ path: safePath, end: true });
  const isActive = !item.soon && Boolean(match);

  if (item.soon) {
    return (
      <span
        className="tl-ks-nav-item tl-ks-nav-item--soon"
        aria-label={`${item.label} (coming soon)`}
        title="Coming soon"
      >
        {item.label}
        <span className="tl-ks-soon-badge" aria-hidden="true">soon</span>
      </span>
    );
  }

  return (
    <Link
      to={item.to}
      className={["tl-ks-nav-item", isActive ? "tl-ks-nav-item--active" : ""].join(" ").trim()}
      aria-current={isActive ? "page" : undefined}
    >
      {item.label}
    </Link>
  );
}

// ── Koshtha ───────────────────────────────────────────────────────────────────

export default function Koshtha({
  role            = "customer",
  userEmail       = "",
  userName        = "",
  onSignOut,
  children,
  passportId      = null,
  accessToken     = null,
  cnCode          = null,
  passportStatus  = null,
  hideOra         = false,
  complianceScope = ["CBAM"],
}) {
  const displayScope = Array.isArray(complianceScope) ? complianceScope : ["CBAM"];
  const navItems     = role === "admin"   ? ADMIN_NAV
                     : role === "auditor" ? AUDITOR_NAV
                     : buildCustomerNav(displayScope);

  return (
    <div className={`tl-koshtha${role === "admin" ? " tl-koshtha--admin" : " tl-koshtha--customer"}`}>

      {/* ── Sidebar ── */}
      <aside
        className="tl-ks-sidebar"
        role="navigation"
        aria-label="Portal navigation"
      >
        {/* Top: wordmark + nav */}
        <div className="tl-ks-sidebar-top">
          <a
            href="https://terralekha.in"
            className="tl-wordmark tl-ks-wordmark"
            aria-label="TERRALEKHA — go to homepage"
          >
            {BRAND_NAME}
          </a>

          {role === "customer" && (
            <div className="tl-ks-framework-pills">
              {displayScope.map((fw) => (
                <span
                  key={fw}
                  className={`tl-ks-framework-pill tl-ks-framework-pill--${fw.toLowerCase()}`}
                  aria-label={`Compliance framework: ${fw}`}
                >
                  {fw}
                </span>
              ))}
            </div>
          )}

          <nav className="tl-ks-nav" aria-label="Main navigation">
            {navItems.map((item) => (
              <NavItem key={item.key} item={item} />
            ))}
          </nav>
        </div>

        {/* Middle: Ora AI panel */}
        {!hideOra && (
          <div className="tl-ks-ora-wrap">
            <OraPanel
              passportId={passportId}
              accessToken={accessToken}
              cnCode={cnCode}
              status={passportStatus}
            />
          </div>
        )}

        {/* Bottom: user name + email + sign out */}
        <footer className="tl-ks-sidebar-footer">
          {(userName || userEmail) && (
            <div className="tl-ks-user-info">
              {userName && <span className="tl-ks-user-name">{userName}</span>}
              {userEmail && (
                <span className="tl-ks-user-email" title={userEmail}>
                  {userEmail}
                </span>
              )}
            </div>
          )}
          <button
            className="tl-sign-out"
            onClick={onSignOut}
            disabled={!onSignOut}
          >
            Sign out
          </button>
        </footer>
      </aside>

      {/* ── Page content ── */}
      <div className="tl-ks-content">
        {children}
      </div>

    </div>
  );
}
